import { useEffect, useRef, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Subscribe to live status updates for a tracking code.
 *
 *   const { status: wsStatus, lastEvent } = useTrackingWebSocket(code, onChange);
 *
 *  - wsStatus: "connecting" | "open" | "closed"
 *  - onChange(event) is called every time the server pushes a status event.
 *  - Automatically reconnects (exponential backoff up to 30s).
 *  - Pauses while the tab is hidden, resumes when visible.
 */
export function useTrackingWebSocket(code, onEvent) {
  const [status, setStatus] = useState("connecting");
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const reconnectTimer = useRef(null);
  const closedByUs = useRef(false);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!code || !BACKEND_URL) return;
    // Reset refs for this mount (StrictMode replays the effect).
    closedByUs.current = false;
    retryRef.current = 0;

    const wsBase = BACKEND_URL.replace(/^http/, "ws").replace(/\/$/, "");
    const url = `${wsBase}/api/ws/track/${encodeURIComponent(code)}`;

    const open = () => {
      if (closedByUs.current) return;
      setStatus("connecting");
      let ws;
      try {
        ws = new WebSocket(url);
      } catch (_) {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        retryRef.current = 0;
        setStatus("open");
      };
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          setLastEvent(data);
          if (data?.type === "status" && onEventRef.current) {
            onEventRef.current(data);
          }
        } catch (_) {/* noop */}
      };
      ws.onerror = () => {
        // Let onclose handle reconnection
      };
      ws.onclose = () => {
        setStatus("closed");
        wsRef.current = null;
        scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      if (closedByUs.current) return;
      if (document.hidden) return;
      const delay = Math.min(30000, 1000 * Math.pow(2, retryRef.current));
      retryRef.current += 1;
      reconnectTimer.current = setTimeout(open, delay);
    };

    const onVisibility = () => {
      if (document.hidden) {
        // Drop the socket while hidden — saves bandwidth.
        if (wsRef.current) {
          closedByUs.current = true;
          try { wsRef.current.close(); } catch (_) {/* noop */}
          wsRef.current = null;
          closedByUs.current = false;
        }
        if (reconnectTimer.current) {
          clearTimeout(reconnectTimer.current);
          reconnectTimer.current = null;
        }
      } else {
        retryRef.current = 0;
        open();
      }
    };

    open();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      closedByUs.current = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (_) {/* noop */}
        wsRef.current = null;
      }
    };
  }, [code]);

  return { status, lastEvent };
}
